import { Stack } from "@mui/material";
import type { GridDataSource } from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import objectHash from "object-hash";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../api/adminApi";
import type { Category } from "../../../../../api/types/accessTypes";
import type { ContactPermissions } from "../../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../../context/useClientContext";
import { pageRoutes } from "../../../../../routes";
import { getReferrerLocationState } from "../../../../../utilities/navigationHelper";
import FundStructureDocumentFilters from "../../common/FundStructureDocumentFilters";
import {
  clearFiltersAction,
  getInitialFundStructureDocumentsFilterState,
} from "../../common/fundStructureDocumentsFilters";
import FundStructureDocumentsGrid from "../../common/FundStructureDocumentsGrid";
import { filterContactDocuments } from "./contactDocumentsFiltering";

interface Props {
  contactId: string;
  categories: Category[];
  contactPermissions: ContactPermissions[];
}

const ContactDetailsDocumentsTab = ({ contactId, categories, contactPermissions }: Props) => {
  const { clientCode } = useClientContext();
  const location = useLocation();
  const navigate = useNavigate();

  const [filterState, setFilterState] = useState(getInitialFundStructureDocumentsFilterState);

  const gridApiRef = useGridApiRef();

  const getAllContactDocuments = useCallback(() => adminApi.getContactDocuments(contactId), [contactId]);

  const [allContactDocuments, fetchError] = useFetch(getAllContactDocuments);

  const [totalRows, setTotalRows] = useState<number>();

  const filterStateHash = objectHash(filterState, { unorderedArrays: true });
  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: () => {
        if (allContactDocuments === undefined) {
          return Promise.resolve({ rows: [] });
        }

        const rows = filterContactDocuments(allContactDocuments, filterState);
        setTotalRows(rows.length);

        return Promise.resolve({ rows, rowCount: rows.length });
      },
    }),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [allContactDocuments, filterStateHash]
  );

  const categoryOptions = useMemo(
    () =>
      categories
        .filter((c) => c.type === "Message")
        .map(({ externalId, name }) => ({ value: externalId, label: name })),
    [categories]
  );

  const investorOptions = useMemo(
    () =>
      distinctBy(
        contactPermissions.map(({ investorName }) => ({
          value: investorName,
          label: investorName,
        })),
        ({ value }) => value
      ),
    [contactPermissions]
  );

  const fundOptions = distinctBy(
    contactPermissions.map(({ fundName }) => ({ value: fundName, label: fundName })),
    ({ value }) => value
  );

  if (fetchError) {
    logError(fetchError, "[ContactDetailsDocumentsTab]");
    return <DataLoadingFailed title="Loading contact documents failed" />;
  }

  const handleClearFilters = () => {
    setFilterState(clearFiltersAction());
  };

  const handleNavigateToDocumentActivity = (postMessageRequestId: string, attachmentId: string) => {
    navigate(
      `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.contacts}/${contactId}/${pageRoutes.messages}/${postMessageRequestId}/${pageRoutes.documents}/${attachmentId}/${pageRoutes.activity}`,
      { state: getReferrerLocationState(location) }
    );
  };

  const isLoading = allContactDocuments === undefined;

  return (
    <Stack spacing={2.5} sx={{ height: "100%" }}>
      <FundStructureDocumentFilters
        categoryOptions={categoryOptions}
        investorOptions={investorOptions}
        fundOptions={fundOptions}
        filterState={filterState}
        onChange={setFilterState}
        onClear={handleClearFilters}
        recordsCount={totalRows ?? 0}
        isLoading={isLoading}
      />

      {isLoading ? (
        <InlineLoader />
      ) : (
        <FundStructureDocumentsGrid
          dataSource={dataSource}
          categories={categories}
          onNavigateToDocumentActivity={handleNavigateToDocumentActivity}
          apiRef={gridApiRef}
        />
      )}
    </Stack>
  );
};

export default ContactDetailsDocumentsTab;
